import { useEffect, useRef, useState } from "react"

/**
 * Frame-loop and geometry primitives for the animated hero and 404 art.
 *
 * Ported from the same kit that drives victordibia.com's hero, so the two
 * sites move in one visual language. Everything is drawn per frame from a
 * small model into SVG attributes - no animation library, no DOM churn, and
 * no React re-render per frame (the draw callback writes attributes directly).
 *
 * The three rules every piece here follows:
 *   1. Under prefers-reduced-motion it renders once, still, and never binds a
 *      pointer listener.
 *   2. It stops when scrolled out of view or the tab is hidden.
 *   3. Colour comes from theme tokens, never hardcoded hex, so light and dark
 *      both work.
 */

export const C = 160 // centre of the 320-unit frame
export const TAU = Math.PI * 2
export const f = (n: number): string => n.toFixed(1)
export const lerp = (a: number, b: number, k: number): number => a + (b - a) * k
export const clamp = (n: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, n))

export interface Pointer {
  x: number
  y: number
  tx: number
  ty: number
  /** eased 0..1 presence, so the art relaxes home instead of snapping */
  on: number
  ton: number
}

export const useReducedMotion = (): boolean => {
  const [reduce, setReduce] = useState(false)
  useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)")
    setReduce(m.matches)
    const on = (e: MediaQueryListEvent) => setReduce(e.matches)
    m.addEventListener("change", on)
    return () => m.removeEventListener("change", on)
  }, [])
  return reduce
}

/**
 * Tracks the pointer in the art's own 320x320 coordinate space. Listens on the
 * nearest [data-hero-zone] ancestor so the art reacts before the cursor
 * physically reaches it, which is what makes it feel alive rather than hoverable.
 */
export const usePointer = (
  ref: React.RefObject<HTMLElement | null>,
  reduce: boolean,
  { w = 320, h = 320 }: { w?: number; h?: number } = {}
) => {
  const state = useRef<Pointer>({ x: w / 2, y: h / 2, tx: w / 2, ty: h / 2, on: 0, ton: 0 })
  useEffect(() => {
    const el = ref.current
    if (!el || reduce) return undefined
    const move = (e: PointerEvent) => {
      const r = el.getBoundingClientRect()
      const s = state.current
      s.tx = ((e.clientX - r.left) / r.width) * w
      s.ty = ((e.clientY - r.top) / r.height) * h
      s.ton = 1
    }
    const leave = () => {
      const s = state.current
      s.ton = 0
      s.tx = w / 2
      s.ty = h / 2
    }
    const zone = (el.closest("[data-hero-zone]") as HTMLElement) || el
    zone.addEventListener("pointermove", move)
    zone.addEventListener("pointerleave", leave)
    return () => {
      zone.removeEventListener("pointermove", move)
      zone.removeEventListener("pointerleave", leave)
    }
  }, [ref, reduce, w, h])
  return state
}

export const smoothPointer = (p: Pointer, k = 0.08): Pointer => {
  p.x = lerp(p.x, p.tx, k)
  p.y = lerp(p.y, p.ty, k)
  p.on = lerp(p.on, p.ton, 0.06)
  return p
}

/**
 * The frame loop. Draws once immediately so SSR-hydrated markup is never blank,
 * then runs only while the element is on screen and the tab is visible.
 */
export const useFrame = (
  ref: React.RefObject<HTMLElement | null>,
  draw: (t: number) => void,
  reduce: boolean
): void => {
  useEffect(() => {
    if (reduce) {
      draw(0)
      return undefined
    }
    let raf = 0
    let running = false
    let visible = true
    let elapsed = 0
    let last = 0
    const tick = (now: number) => {
      elapsed += (now - last) / 1000
      last = now
      draw(elapsed)
      raf = requestAnimationFrame(tick)
    }
    const start = () => {
      if (running) return
      running = true
      last = performance.now()
      raf = requestAnimationFrame(tick)
    }
    const stop = () => {
      if (!running) return
      running = false
      cancelAnimationFrame(raf)
    }
    const sync = () =>
      visible && document.visibilityState === "visible" ? start() : stop()
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting
      sync()
    })
    if (ref.current) io.observe(ref.current)
    document.addEventListener("visibilitychange", sync)
    draw(0)
    sync()
    return () => {
      stop()
      io.disconnect()
      document.removeEventListener("visibilitychange", sync)
    }
  }, [ref, draw, reduce])
}

/** Catmull-Rom through the points, emitted as a closed cubic path. */
export const closedPath = (P: Array<[number, number]>): string => {
  const n = P.length
  const d = [`M${f(P[0][0])},${f(P[0][1])}`]
  for (let i = 0; i < n; i++) {
    const p0 = P[(i - 1 + n) % n]
    const p1 = P[i]
    const p2 = P[(i + 1) % n]
    const p3 = P[(i + 2) % n]
    d.push(
      `C${f(p1[0] + (p2[0] - p0[0]) / 6)},${f(p1[1] + (p2[1] - p0[1]) / 6)} ${f(
        p2[0] - (p3[0] - p1[0]) / 6
      )},${f(p2[1] - (p3[1] - p1[1]) / 6)} ${f(p2[0])},${f(p2[1])}`
    )
  }
  return d.join(" ") + "Z"
}

/** An organic closed shape that breathes and leans toward the pointer. */
export const blob = ({
  t, n = 10, base, amp, speed, seed, p, pull = 0, cx = C, cy = C,
}: {
  t: number; n?: number; base: number; amp: number; speed: number
  seed: number; p: Pointer; pull?: number; cx?: number; cy?: number
}): string => {
  const ang = Math.atan2(p.y - cy, p.x - cx)
  const dist = Math.min(1, Math.hypot(p.x - cx, p.y - cy) / 160)
  const P: Array<[number, number]> = []
  for (let i = 0; i < n; i++) {
    const a = (i / n) * TAU
    let r =
      base +
      amp *
        (Math.sin(t * speed + i * 1.7 + seed) +
          0.55 * Math.sin(t * speed * 1.6 - i * 2.3 + seed * 2))
    r += pull * p.on * dist * Math.cos(a - ang) * base * 0.35
    P.push([cx + r * Math.cos(a), cy + r * Math.sin(a)])
  }
  return closedPath(P)
}

/** Evenly spaced points on a circle - the agent ring most variants build on. */
export const ring = (n: number, radius: number, phase = 0, cx = C, cy = C) =>
  Array.from({ length: n }, (_, i) => {
    const a = phase + (i / n) * TAU
    return { x: cx + radius * Math.cos(a), y: cy + radius * Math.sin(a), a }
  })

/** Quadratic edge bowed toward the centre, so a hub-and-spoke reads as flow. */
export const bowedEdge = (
  x1: number, y1: number, x2: number, y2: number, bow = 0.18,
  cx = C, cy = C
): string => {
  const mx = (x1 + x2) / 2
  const my = (y1 + y2) / 2
  return `M${f(x1)},${f(y1)} Q${f(lerp(mx, cx, bow))},${f(lerp(my, cy, bow))} ${f(x2)},${f(y2)}`
}
